import React, { useMemo, useEffect, useCallback, useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconButton, Card, FakeText } from '@totalsoft/rocket-ui';
import { Save } from '@mui/icons-material';
import { TableBody, TableRow, TableHead, TableCell } from '@mui/material';
import { StyledHeaderCell, StyledTable, StyledTableRow } from 'components/common/Styles';
import { ADMIN_DMS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';
import GroupIcon from '@mui/icons-material/Group';
import { CONTENT_TYPE_X_USER_GROUPS, USER_GROUPS_LIST_QUERY } from '../../queries';
import { SAVE_CONTENT_TYPE_X_USER_GROUPS } from '../../mutations';
import { CONTENT_TYPE_X_USER_GROUPS_SAVED } from '../../subscriptions';
import ContentTypeXUserGroupComponent from './ContentTypeXUserGroupComponent';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { ServiceContext } from 'providers/ServiceProvider';
import { useToast } from 'hooks/toasts/useToast';

const ContentTypeXUserGroupContainer = ({ contentTypeId }) => {
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [activeService] = useContext(ServiceContext);
  const canWrite = usePermission(ADMIN_DMS_WRITE);
  const addToast = useToast();
  const { t } = useTranslation();

  const { data, loading } = useQuery(CONTENT_TYPE_X_USER_GROUPS, {
    skip: !contentTypeId,
    variables: { contentTypeId }
  });

  const { data: userGroups } = useQuery(USER_GROUPS_LIST_QUERY, {
    fetchPolicy: 'network-only'
  });

  const combinedList = useMemo(() => {
    if (!data?.contentTypeXUserGroups || !userGroups?.userGroupsList) return [];

    const userGroupsOptions = userGroups?.userGroupsList;
    const contentTypeXUserGroups = data?.contentTypeXUserGroups;

    const result = userGroupsOptions.map(option => {
      const userGroupId = option.id;
      const userGroup = contentTypeXUserGroups.find(group => group.userGroupId === userGroupId);

      return {
        contentTypeId,
        userGroupId,
        canRead: userGroup ? userGroup.canRead : false,
        canWrite: userGroup ? userGroup.canWrite : false,
        canDelete: userGroup ? userGroup.canDelete : false,
        canVerify: userGroup ? userGroup.canVerify : false,
        groupName: option.name
      };
    });

    return result;
  }, [contentTypeId, data?.contentTypeXUserGroups, userGroups?.userGroupsList]);

  const [contentTypeXUserGroups, setContentTypeXUserGroups] = useState(combinedList);
  const contentTypeXUserGroupsRef = useRef([]);

  useEffect(() => {
    setContentTypeXUserGroups(combinedList);
    contentTypeXUserGroupsRef.current = combinedList;
  }, [combinedList]);

  const [saveContentTypeXUserGroups] = useMutation(SAVE_CONTENT_TYPE_X_USER_GROUPS, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleRefChange = useCallback((userGroupId, property, value) => {
    contentTypeXUserGroupsRef.current = contentTypeXUserGroupsRef.current.map(item =>
      item.userGroupId === userGroupId ? { ...item, [property]: value } : item
    );
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(() => {
    // eslint-disable-next-line no-unused-vars
    const sanitizedDataArray = contentTypeXUserGroupsRef.current.map(({ groupName, canWrite, canRead, ...rest }) => ({
      ...rest,
      canWrite: canWrite || false,
      canRead: canRead || false
    }));

    saveContentTypeXUserGroups({
      variables: {
        input: sanitizedDataArray
      }
    });

    setSaving(true);
  }, [saveContentTypeXUserGroups]);

  useSubscription(CONTENT_TYPE_X_USER_GROUPS_SAVED, {
    onData: ({
      data: {
        data: { contentTypeXUserGroupsSaved }
      },
      client
    }) => {
      setContentTypeXUserGroups(contentTypeXUserGroupsSaved?.contentTypeXUserGroups);
      contentTypeXUserGroupsRef.current = contentTypeXUserGroupsSaved?.contentTypeXUserGroups;

      client.writeQuery({
        query: CONTENT_TYPE_X_USER_GROUPS,
        context: { serviceName: activeService?.name },
        variables: { contentTypeId },
        data: { contentTypeXUserGroups: contentTypeXUserGroupsSaved?.contentTypeXUserGroups }
      });

      setSaving(false);
      setIsDirty(false);
      addToast(t('UserGroups.UserGroupsSaved'), 'success');
    }
  });

  return (
    <Card
      icon={GroupIcon}
      iconColor='primary'
      title={t('UserGroups.Title')}
      actions={
        <>
          <IconButton
            type='save'
            fontSize='medium'
            color='transparent'
            title={t('SaveChanges')}
            onClick={handleSave}
            disabled={!canWrite || saving || !isDirty}
          >
            <Save />
          </IconButton>
        </>
      }
    >
      <StyledTable>
        <TableHead>
          <StyledTableRow>
            <StyledHeaderCell colSpan={1}>{t('UserGroups.Title')}</StyledHeaderCell>
            <StyledHeaderCell colSpan={1}>{t('AllowRead')}</StyledHeaderCell>
            <StyledHeaderCell colSpan={1}>{t('AllowWrite')}</StyledHeaderCell>
            <StyledHeaderCell colSpan={1}>{t('AllowDelete')}</StyledHeaderCell>
            <StyledHeaderCell colSpan={1}>{t('AllowVerify')}</StyledHeaderCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {loading && (
              <TableCell colSpan={5}>
                <FakeText lines={5} />
              </TableCell>
            )}
          </TableRow>
          {!loading &&
            contentTypeXUserGroups &&
            contentTypeXUserGroups.length > 0 &&
            contentTypeXUserGroups.map((contentTypeXUserGroup, index) => {
              return (
                <ContentTypeXUserGroupComponent
                  key={contentTypeXUserGroup.userGroupId}
                  index={index}
                  contentTypeXUserGroup={contentTypeXUserGroup}
                  userGroups={userGroups}
                  canWrite={canWrite}
                  onChange={handleRefChange}
                  saving={saving}
                />
              );
            })}
        </TableBody>
      </StyledTable>
    </Card>
  );
};

ContentTypeXUserGroupContainer.propTypes = {
  contentTypeId: PropTypes.number
};

export default ContentTypeXUserGroupContainer;
